import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_check_filled } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import { useEffect, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import apis from '../../../modules/apis'
import { axiosGet } from '../../../modules/axiosHelper'
import { SHOW_ADDITIONAL_FEATURES } from '../../../modules/consts/trial'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import useWebviewNavigation from '../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_common, n_lesson_prep, n_upcoming_lessons } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { COLORS } from '../../../modules/vars'
import { ContentBody, ContentSubTab } from '../../../pages/[lang]/portal/lessons/preparation-over/[lessonId]'
import { useLessonsStack } from '../../../pages/[lang]/webview/lessons'
import SectionAIReviewCall from '../../new-portal/lessons/SectionAIReviewCall'
import WebviewStackContainer from '../common/WebviewStackContainer'
import { Title } from './LessonPreparationActivity'

interface ILessonPreparationOverActivity {
  params: {
    lessonId: string
  }
}

const LessonPreparationOverActivity: ActivityComponentType = ({ params }: ILessonPreparationOverActivity) => {
  const { currentUser } = useCurrentUser()
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const [lesson, setLesson] = useState(null)
  const [expressionData, setExpressionData] = useState(null)
  const [currentHash, setCurrentHash] = useState(1)
  const [lessonStartDate, setLessonDate] = useState('')
  const [lessonStartTime, setLessonTime] = useState('')
  const pathParams = { lessonId: Number(params.lessonId) }
  const { popBackToRingleApp } = useWebviewNavigation()
  const appRouter = useAppRouter()
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const router = useAppRouter({ stackPush: push })
  const { sendPageViewEvent } = useEventTracking()
  const getFullUrl = useReturnFullUrl()
  const { t } = useTranslation()
  const { sendClickEvent } = useEventTracking()

  useEffect(() => {
    const fetchData = async () => {
      const response = await axiosGet(apis.lessons.preparation.getV5(pathParams))

      if (response) {
        setLesson(response.lesson)
        setLessonDate(response.lesson.formatted_start_date)
        setLessonTime(response.lesson.formatted_start_time_only)
        setExpressionData(response.lesson.expressions)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (!router) return
    sendPageViewEvent({ page_name: 'lesson_prep_done' })
  }, [router.asPath])

  const onClickLessonStyle = () => {
    router.push(urls.webview.lessons.lesson_style(params.lessonId), {
      stackActivity: 'LessonStyleActivity',
      stackSendData: {
        lessonId: params.lessonId,
      },
    })
  }

  return (
    <WebviewStackContainer
      onBack={pop}
      title={<Title lessonStartDate={lessonStartDate} lessonStartTime={lessonStartTime} />}
    >
      {lesson && expressionData && (
        <div className="relative h-full">
          <div className="mt-[40px] flex w-full flex-col items-center justify-center space-y-20">
            {/** 문구  */}
            <div className="flex flex-col items-center justify-center">
              <QIcon_check_filled className="mb-[12px] h-56 w-56 text-green-500" />
              <div className=" mb-[4px]">
                <span className="text-[16px] font-bold text-green-500">{t(n_lesson_prep.complete.title_done)}</span>
              </div>
              <div className="flex flex-col items-center justify-center">
                <div className="text-align-center items-center justify-center text-center text-[28px] font-bold text-black">
                  {t(n_lesson_prep.complete.desc(currentUser?.profile?.name))}
                </div>
              </div>
            </div>

            {/* 모바일에서만 보이는 AI 리뷰 콜 섹션 */}
            {(currentUser?.is_ringle_member || SHOW_ADDITIONAL_FEATURES) && (
              <div className="block sm:hidden px-[16px] mt-[24px]">
                <SectionAIReviewCall />
              </div>
            )}

            {/** 콘텐츠 */}
            <div className="z-60 flex flex-col items-center border-gray-200 bg-white sm:flex sm:w-full">
              <ContentSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />
            </div>
            <div className={`mx-auto mb-[40px] w-full max-w-[289px] overflow-y-auto sm:max-w-[689px]`}>
              <ContentBody
                lesson={lesson}
                activeTabIdx={activeTabIdx}
                lessonId={params.lessonId}
                expressionData={expressionData}
                currentHash={currentHash}
              />
            </div>
          </div>

          <div
            className={`sticky bottom-[0px] z-20 flex w-full justify-center space-x-[8px] bg-white pb-[40px] pt-16  ${
              activeTabIdx !== 0 ? 'mt-[40px]' : 'mt-0'
            } `}
          >
            <div>
              <DSButton
                onClick={onClickLessonStyle}
                size={BUTTON_SIZE.LG}
                type={BUTTON_TYPE.OUTLINE}
                customBorderColor={COLORS.GRAY300}
              >
                <DSButton.Label>{t(n_upcoming_lessons.btn_lesson_style)}</DSButton.Label>
              </DSButton>
            </div>
            <div>
              <DSButton
                onClick={() => {
                  sendClickEvent({ button_name: 'lesson_done_exit' })
                  router.appScreenReset({
                    appScreenName: APP_SCREEN_NAME.MAIN_TABS,
                    appSendData: { screen: APP_SCREEN_NAME.LESSON_SCREEN },
                  })
                }}
                size={BUTTON_SIZE.LG}
                type={BUTTON_TYPE.SOLID}
                customBorderColor={COLORS.PURPLE600}
              >
                <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
              </DSButton>
            </div>
          </div>
        </div>
      )}
    </WebviewStackContainer>
  )
}

export default LessonPreparationOverActivity
